import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { AlertItem } from '@/components/alerts/AlertItem'
import { EventItem } from '@/components/events/EventItem'
import { ProfitTakingItem } from '@/components/profitTaking/ProfitTakingItem'
import { RemindersWidget } from '@/components/dashboard/RemindersWidget'
import { useAlertList } from '@/lib/useAlerts'
import { useEmergencyToggle } from '@/lib/useEmergency'
import { useEventsUpcoming } from '@/lib/useEvents'
import { formatNumberDe } from '@/lib/format'
import { formatPnl, pnlTone, sumPnlEur } from '@/lib/pnl'
import { listPositions } from '@/lib/positionsApi'
import { useMe } from '@/lib/useMe'
import { useProfitTakingTop } from '@/lib/useProfitTaking'
import { useCurrentStrategy } from '@/lib/useStrategy'
import { useOnboardingStore } from '@/stores/onboarding'

const PNL_TONE_TEXT = {
  gain: 'text-gain',
  loss: 'text-loss',
  neutral: 'text-ink',
} as const

function isAxiosNotFound(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof (err as { response?: { status?: number } }).response?.status === 'number' &&
    (err as { response: { status: number } }).response.status === 404
  )
}

export function DashboardPage() {
  const navigate = useNavigate()
  const { data: user, isLoading, isError, error } = useMe()
  const alertsQuery = useAlertList({ unread_only: true, limit: 3 })
  const profitTakingQuery = useProfitTakingTop(3)
  const eventsQuery = useEventsUpcoming(7)
  const strategyQuery = useCurrentStrategy()
  const positionsQuery = useQuery({ queryKey: ['positions'], queryFn: listPositions })
  const totals = sumPnlEur(positionsQuery.data ?? [])
  const dismissed = useOnboardingStore((s) => s.dismissed)
  const resume = useOnboardingStore((s) => s.resume)
  const emergencyToggle = useEmergencyToggle()

  const noStrategy = strategyQuery.isError && isAxiosNotFound(strategyQuery.error)

  // Auto-Redirect zum Onboarding, solange noch keine Strategy existiert
  // und der User den Wizard nicht explizit verlassen hat. Sobald er ihn
  // verlässt, bleibt das Dashboard sichtbar mit Banner zum Fortsetzen.
  useEffect(() => {
    if (noStrategy && !dismissed) {
      navigate('/onboarding', { replace: true })
    }
  }, [noStrategy, dismissed, navigate])

  if (noStrategy && !dismissed) {
    // Effect läuft post-Render; kein Flicker.
    return <Navigate to="/onboarding" replace />
  }

  return (
    <AppShell>
      {isLoading && <p className="font-mono text-sm text-muted">Lade Benutzerdaten…</p>}

      {isError && (
        <div role="alert" className="border border-loss/40 bg-loss/5 px-4 py-3">
          <p className="tracked-caps text-[10px] text-loss">Fehler</p>
          <p className="mt-2 font-mono text-sm text-ink">
            Benutzerdaten konnten nicht geladen werden.
          </p>
          <p className="mt-1 font-mono text-xs text-muted">{error.message}</p>
        </div>
      )}

      {noStrategy && dismissed && (
        <div
          role="status"
          className="mb-6 flex max-w-3xl flex-col items-stretch gap-3 border border-bernstein/40 bg-bernstein/5 px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-6"
        >
          <div>
            <p className="tracked-caps text-[10px] text-bernstein">Onboarding pausiert</p>
            <p className="mt-1 font-mono text-sm text-ink">
              Noch keine Strategie aktiv. Wizard fortsetzen, um Sextant einsatzbereit zu machen.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              resume()
              navigate('/onboarding')
            }}
            className="font-mono text-xs text-bernstein transition-colors hover:text-ink"
          >
            Fortsetzen →
          </button>
        </div>
      )}

      {user && (
        <article className="fade-in max-w-3xl space-y-10">
          {user.emergency_mode_active && user.emergency_mode_activated_at && (
            <section
              className="mb-6 flex flex-col items-stretch gap-3 border border-loss/40 bg-loss/5 px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-6"
              role="alert"
            >
              <div>
                <p className="tracked-caps text-[10px] text-loss">Emergency-Mode aktiv</p>
                <p className="mt-1 text-sm text-ink">
                  🚨 Emergency-Mode aktiv seit{' '}
                  {new Date(user.emergency_mode_activated_at).toLocaleDateString('de-DE')}
                </p>
                <p className="mt-1 font-mono text-xs text-muted">
                  Buy-Empfehlungen mit Bedenkzeit-Hinweis · Stop-Trigger als Manual Review
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Emergency-Mode deaktivieren?')) {
                    emergencyToggle.mutate('deactivate')
                  }
                }}
                disabled={emergencyToggle.isPending}
                className="tracked-caps border border-loss px-4 py-2 text-[11px] text-loss transition-colors hover:bg-loss hover:text-surface disabled:opacity-50"
              >
                Deaktivieren
              </button>
            </section>
          )}

          <section>
            <h2 className="tracked-caps mb-4 text-[10px] text-faint">Session</h2>
            <dl className="font-mono text-sm">
              <div className="hairline-b flex justify-between py-2.5">
                <dt className="text-muted">User</dt>
                <dd className="text-ink">{user.username}</dd>
              </div>
              <div className="hairline-b flex justify-between py-2.5">
                <dt className="text-muted">Zeitzone</dt>
                <dd className="text-ink">{user.timezone}</dd>
              </div>
              <div className="hairline-b flex justify-between py-2.5">
                <dt className="text-muted">Status</dt>
                <dd className="text-gain">aktiv</dd>
              </div>
            </dl>
          </section>

          <section>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="tracked-caps text-[10px] text-faint">Portfolio</h2>
              <Link
                to="/positions"
                className="font-mono text-[10px] text-muted transition-colors hover:text-bernstein"
              >
                alle Positionen →
              </Link>
            </div>
            {positionsQuery.isLoading ? (
              <p className="font-mono text-sm text-muted">Lade…</p>
            ) : positionsQuery.isError ? (
              <p className="font-mono text-sm text-loss">
                Positionen konnten nicht geladen werden.
              </p>
            ) : (positionsQuery.data?.length ?? 0) === 0 ? (
              <p className="font-mono text-sm text-muted">Noch keine Positionen.</p>
            ) : (
              <dl className="font-mono text-sm" data-numeric>
                <div className="hairline-b flex justify-between py-2.5">
                  <dt className="text-muted">Positionen</dt>
                  <dd className="text-ink">{positionsQuery.data?.length}</dd>
                </div>
                <div className="hairline-b flex justify-between py-2.5">
                  <dt className="text-muted">Wert</dt>
                  <dd className="text-ink">
                    {totals.valueEur === null ? '—' : `${formatNumberDe(totals.valueEur)} €`}
                  </dd>
                </div>
                <div className="hairline-b flex justify-between py-2.5">
                  <dt className="text-muted">G/V{totals.incomplete ? ' (unvollständig)' : ''}</dt>
                  <dd
                    className={
                      PNL_TONE_TEXT[pnlTone(totals.pnlEur === null ? null : String(totals.pnlEur))]
                    }
                  >
                    {formatPnl(
                      totals.pnlEur === null ? null : String(totals.pnlEur),
                      totals.pct === null ? null : String(totals.pct),
                      'EUR',
                    )}
                  </dd>
                </div>
              </dl>
            )}
          </section>

          <section>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="tracked-caps text-[10px] text-faint">Alerts</h2>
              <Link
                to="/alerts"
                className="font-mono text-[10px] text-muted transition-colors hover:text-bernstein"
              >
                alle ansehen →
              </Link>
            </div>
            {alertsQuery.isLoading ? (
              <p className="font-mono text-sm text-muted">Lade…</p>
            ) : alertsQuery.isError ? (
              <p className="font-mono text-sm text-loss">Alerts konnten nicht geladen werden.</p>
            ) : (alertsQuery.data?.items.length ?? 0) === 0 ? (
              <p className="font-mono text-sm text-muted">Keine offenen Alerts.</p>
            ) : (
              <div className="flex flex-col">
                {alertsQuery.data!.items.map((alert) => (
                  <AlertItem key={alert.id} alert={alert} compact />
                ))}
                {(alertsQuery.data?.unread_count ?? 0) > 3 && (
                  <p className="mt-3 font-mono text-xs text-faint">
                    + {(alertsQuery.data?.unread_count ?? 0) - 3} weitere ungelesen
                  </p>
                )}
              </div>
            )}
          </section>

          <RemindersWidget />

          <section>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="tracked-caps text-[10px] text-faint">Profit-Taking</h2>
              <Link
                to="/profit-taking"
                className="font-mono text-[10px] text-muted transition-colors hover:text-bernstein"
              >
                alle ansehen →
              </Link>
            </div>
            {profitTakingQuery.isLoading ? (
              <p className="font-mono text-sm text-muted">Lade…</p>
            ) : profitTakingQuery.isError ? (
              <p className="font-mono text-sm text-loss">Profit-Taking-Daten nicht verfügbar.</p>
            ) : (profitTakingQuery.data?.items.length ?? 0) === 0 ? (
              <p className="font-mono text-sm text-muted">Keine offenen Trigger.</p>
            ) : (
              <div className="flex flex-col">
                {profitTakingQuery.data!.items.map((p) => (
                  <ProfitTakingItem key={p.position_id} position={p} compact />
                ))}
                {(profitTakingQuery.data?.total_triggers ?? 0) >
                  (profitTakingQuery.data?.items.length ?? 0) && (
                  <p className="mt-3 font-mono text-xs text-faint">
                    +{' '}
                    {(profitTakingQuery.data?.total_triggers ?? 0) -
                      (profitTakingQuery.data?.items.length ?? 0)}{' '}
                    weitere Trigger
                  </p>
                )}
              </div>
            )}
          </section>

          <section>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="tracked-caps text-[10px] text-faint">Events</h2>
              <Link
                to="/events"
                className="font-mono text-[10px] text-muted transition-colors hover:text-bernstein"
              >
                alle ansehen →
              </Link>
            </div>
            {eventsQuery.isLoading ? (
              <p className="font-mono text-sm text-muted">Lade…</p>
            ) : eventsQuery.isError ? (
              <p className="font-mono text-sm text-loss">Events konnten nicht geladen werden.</p>
            ) : (eventsQuery.data?.items.length ?? 0) === 0 ? (
              <p className="font-mono text-sm text-muted">Keine Events in den nächsten 7 Tagen.</p>
            ) : (
              <div className="flex flex-col">
                {eventsQuery.data!.items.slice(0, 3).map((event) => (
                  <EventItem key={event.id} event={event} compact />
                ))}
                {(eventsQuery.data?.total ?? 0) > 3 && (
                  <p className="mt-3 font-mono text-xs text-faint">
                    + {(eventsQuery.data?.total ?? 0) - 3} weitere Events
                  </p>
                )}
              </div>
            )}
          </section>

          <section>
            <h2 className="tracked-caps mb-4 text-[10px] text-faint">Build</h2>
            <dl className="font-mono text-sm">
              <div className="hairline-b flex justify-between py-2.5">
                <dt className="text-muted">Phase</dt>
                <dd className="text-ink">1a · MVP-Core</dd>
              </div>
              <div className="hairline-b flex justify-between py-2.5">
                <dt className="text-muted">Scope</dt>
                <dd className="text-ink">Dashboard-Skeleton</dd>
              </div>
            </dl>
          </section>

          {user && !user.emergency_mode_active && (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    'Emergency-Mode aktivieren? Buy-Empfehlungen bekommen einen Bedenkzeit-Hinweis, Stop-Trigger werden zu Manual-Review.',
                  )
                ) {
                  emergencyToggle.mutate('activate')
                }
              }}
              disabled={emergencyToggle.isPending}
              className="mt-10 border border-hairline px-3 py-2 text-left font-mono text-xs text-faint transition-colors hover:text-loss disabled:opacity-50"
            >
              Emergency-Mode aktivieren
            </button>
          )}
        </article>
      )}
    </AppShell>
  )
}
